import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { isAllowedAttachmentFile } from '@common/constants/attachment-file-types';
import type { IR2Attachment, IR2FileLimitsResponse } from '@common/types/api/r2-types';
import { R2AttachmentType } from '@common/types/api/r2-types';
import { Core } from '@core/core';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { v4 as uuidV4 } from 'uuid';
import { R2FileService } from './r2-file.service';

export type DraftAttachmentStatus = 'uploading' | 'ready' | 'error';

export interface DraftAttachment
{
    id: string;
    name: string;
    size: number;
    mimeType: string;
    status: DraftAttachmentStatus;
    /** i18n key for the error label; present only when status === 'error'. */
    errorKey?: string;
    errorParams?: Record<string, unknown>;
    /** Resolved attachment payload; present only when status === 'ready'. */
    attachment?: IR2Attachment;
    /** Server operation id; present while an upload is in flight so a poll can resume after a reload. */
    operationId?: number;
    /**
     * When true the chip offers an "Upload" action and re-opens the file picker
     * on click — used for uploads that failed or that could not be resumed after
     * a reload (the original File is gone, so the user must re-select it).
     */
    reuploadable?: boolean;
}

/** localStorage key under which the composer's draft attachments are persisted. */
const STORAGE_KEY = 'r2.chat.attachments';

/**
 * Holds the chat composer's draft file attachments. Ported from r2-chat-web's
 * ThreadAttachmentStore, simplified to a single bucket since the addin shows one
 * conversation per document. Chips appear the moment an upload starts and
 * transition through uploading → ready/error in place; {@link readyAttachments}
 * is read by OfficeChatV1.buildSendRequest when a message is sent, and
 * {@link clear} is called once the send succeeds.
 *
 * The bucket is mirrored to localStorage so attached files survive a reload:
 * ready files are shown as-is, in-flight uploads resume polling by their
 * persisted operation id, and anything else becomes a re-uploadable error chip
 * since the original File object cannot be restored.
 */
@Injectable({ providedIn: 'root' })
export class ThreadAttachmentStore
{
    public static readonly POWERPOINT_CONTEXT_LIMIT_ERROR_KEY = 'CHAT.ATTACH-FILE.powerPointContextLimit';

    private readonly _fileService = inject(R2FileService);
    private readonly _core = inject(Core);
    private readonly _translate = inject(TranslateService);

    private readonly _drafts = signal<DraftAttachment[]>(this._hydrate());

    readonly attachments = this._drafts.asReadonly();
    readonly readyAttachments = computed<IR2Attachment[]>(
        () => this._drafts()
            .filter((a) => a.status === 'ready' && a.attachment)
            .map((a) => a.attachment!),
    );
    readonly uploading = computed(() => this._drafts().some((a) => a.status === 'uploading'));
    readonly hasReady = computed(() => this._drafts().some((a) => a.status === 'ready'));

    constructor()
    {
        this._resumeUploads(this._drafts());

        // Mirror every mutation to localStorage so the composer restores on reload.
        effect(() => this._persist(this._drafts()));
    }

    async addFiles(files: File[]): Promise<void>
    {
        if (!files.length) return;

        const copilotId = this._core.subscription.getActiveCopilot()?.id ?? '';
        if (!copilotId) return;

        // Re-adding a file by name replaces the existing chip (regardless of its
        // current status), so a corrupted or stale entry is superseded by the
        // fresh upload rather than duplicated.
        const incomingNames = new Set(files.map((f) => f.name));
        this._removeByNames(incomingNames);

        let limits: IR2FileLimitsResponse;
        try
        {
            limits = await this._fileService.getLimits();
        }
        catch
        {
            limits = {} as IR2FileLimitsResponse;
        }

        const maxCount = limits.attachments_count_max ?? Infinity;
        const documentAttachmentCount = this._documentAttachmentCount();
        const maxFileSize = limits.upload_file_size_max ?? Infinity;
        const maxTotalSize = limits.upload_storage_files_size_max ?? Infinity;
        this._clearContextLimitError();

        // Error chips don't consume any quota.
        const valid = this._drafts().filter((a) => a.status !== 'error');
        let count = valid.length + documentAttachmentCount;
        let totalSize = valid.reduce((sum, a) => sum + a.size, 0);

        // Validate + append chips synchronously up front so quota accounting stays
        // deterministic, then collect the files that actually need uploading.
        const queue: { id: string; file: File; }[] = [];

        // Files rejected purely because the count cap was reached are collapsed
        // into a single aggregated error chip rather than one chip per file.
        let tooManyCount = 0;

        for (const file of files)
        {
            const id = uuidV4();
            const base: DraftAttachment = {
                id,
                name: file.name,
                size: file.size,
                mimeType: file.type || 'application/octet-stream',
                status: 'uploading',
            };

            if (!isAllowedAttachmentFile(file.name, file.type))
            {
                this._append({ ...base, status: 'error', errorKey: 'CHAT.ATTACH-FILE.unsupportedType' });
                continue;
            }
            if (count >= maxCount)
            {
                tooManyCount++;
                continue;
            }
            if (file.size === 0)
            {
                this._append({ ...base, status: 'error', errorKey: 'CHAT.ATTACH-FILE.emptyFile' });
                continue;
            }
            if (file.size > maxFileSize)
            {
                this._append({ ...base, status: 'error', errorKey: 'CHAT.ATTACH-FILE.fileTooLarge' });
                continue;
            }
            if (totalSize + file.size > maxTotalSize)
            {
                this._append({ ...base, status: 'error', errorKey: 'CHAT.ATTACH-FILE.storageFull' });
                continue;
            }

            count++;
            totalSize += file.size;
            this._append(base);
            queue.push({ id, file });
        }

        if (tooManyCount > 0)
        {
            this._appendCountError(tooManyCount, maxCount, documentAttachmentCount > 0);
        }

        for (const { id, file } of queue)
        {
            await this._upload(id, file, copilotId);
        }

        // Parallel alternative — kept for when concurrent uploads are wanted again:
        // await Promise.all(queue.map(({ id, file }) => this._upload(id, file, copilotId)));
    }

    /** Document snapshots belong to one send/run, not the composer's draft files. */
    async uploadContextFile(file: File): Promise<IR2Attachment>
    {
        const copilotId = this._core.subscription.getActiveCopilot()?.id;
        if (!copilotId) throw new Error('No active copilot');
        return lastValueFrom(this._fileService.uploadFile(file, copilotId));
    }

    async validateSendAttachments(attachments: IR2Attachment[]): Promise<void>
    {
        if (this._core.office.getHost().getTag() !== 'powerpoint')
        {
            return;
        }

        const files = attachments.filter(attachment => attachment.type === R2AttachmentType.File);
        if (!files.length)
        {
            this._clearContextLimitError();
            return;
        }

        let limits: IR2FileLimitsResponse;
        try
        {
            limits = await this._fileService.getLimits();
        }
        catch
        {
            return;
        }

        const maxCount = limits.attachments_count_max ?? Infinity;
        if (files.length > maxCount)
        {
            const error = this._appendCountError(
                files.length - maxCount,
                maxCount,
                files.some(file => file.name === 'powerpoint-current-slide.png'),
            );
            throw new Error(this._translate.instant(error.errorKey!, error.errorParams));
        }
        this._clearContextLimitError();
    }

    removeAttachment(id: string): void
    {
        this._drafts.update((list) => list.filter((a) => a.id !== id));
    }

    removeAttachmentByName(name: string): void
    {
        this._removeByNames(new Set([ name ]));
    }

    clear(): void
    {
        if (this._drafts().length) this._drafts.set([]);
    }

    private _documentAttachmentCount(): number
    {
        if (this._core.office.getHost().getTag() !== 'powerpoint'
            || !Office.context.requirements.isSetSupported('PowerPointApi', '1.8'))
        {
            return 0;
        }

        const documentEnabled = this._core.chatSetting.getCurrentSkill()?.documentContextEnabled
            ?? this._core.chatAction.getActions().attachDocument.value;
        return documentEnabled ? 1 : 0;
    }

    private _appendCountError(count: number, max: number, documentContext: boolean): DraftAttachment
    {
        const errorKey = documentContext ? ThreadAttachmentStore.POWERPOINT_CONTEXT_LIMIT_ERROR_KEY : 'CHAT.ATTACH-FILE.tooManyFiles';
        const error: DraftAttachment = {
            id: uuidV4(),
            name: '',
            size: 0,
            mimeType: 'application/octet-stream',
            status: 'error',
            errorKey,
            errorParams: { count, max },
        };
        this._drafts.update(list => [ ...list.filter(attachment => !documentContext || attachment.errorKey !== errorKey), error ]);
        return error;
    }

    private _clearContextLimitError(): void
    {
        this._drafts.update(list => list.filter(attachment => attachment.errorKey !== ThreadAttachmentStore.POWERPOINT_CONTEXT_LIMIT_ERROR_KEY));
    }

    private async _upload(id: string, file: File, copilotId: string): Promise<void>
    {
        try
        {
            const attachment = await lastValueFrom(
                this._fileService.uploadFile(file, copilotId, (operationId) =>
                    this._patch(id, { operationId }),
                ),
            );
            this._patch(id, { status: 'ready', attachment });
        }
        catch (err)
        {
            console.error('File upload failed:', err);
            this._patch(id, { status: 'error', reuploadable: true, errorKey: undefined, operationId: undefined });
        }
    }

    private _removeByNames(names: Set<string>): void
    {
        this._drafts.update((list) =>
        {
            if (!list.some((a) => a.name && names.has(a.name))) return list;
            return list.filter((a) => !(a.name && names.has(a.name)));
        });
    }

    private _hydrate(): DraftAttachment[]
    {
        try
        {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return [];
            const parsed: unknown = JSON.parse(raw);
            if (!Array.isArray(parsed)) return [];
            return parsed
                .map((item) => this._restoreChip(item as Partial<DraftAttachment>))
                .filter((chip): chip is DraftAttachment => chip !== null);
        }
        catch
        {
            return [];
        }
    }

    private _restoreChip(item: Partial<DraftAttachment>): DraftAttachment | null
    {
        if (!item || typeof item.name !== 'string' || typeof item.id !== 'string') return null;

        const base: DraftAttachment = {
            id: item.id,
            name: item.name,
            size: typeof item.size === 'number' ? item.size : 0,
            mimeType: typeof item.mimeType === 'string' ? item.mimeType : 'application/octet-stream',
            status: 'error',
        };

        // Finished uploads are shown as-is.
        if (item.status === 'ready' && item.attachment)
        {
            return { ...base, status: 'ready', attachment: item.attachment };
        }

        // In-flight uploads with a known operation id keep polling after reload.
        if (item.status === 'uploading' && typeof item.operationId === 'number')
        {
            return { ...base, status: 'uploading', operationId: item.operationId };
        }

        // Validation errors (e.g. empty file) keep their original message.
        if (item.status === 'error' && item.errorKey && !item.reuploadable)
        {
            return { ...base, status: 'error', errorKey: item.errorKey, errorParams: item.errorParams };
        }

        // Everything else — failed, or an upload that never started — becomes a
        // re-uploadable chip: the file must be re-selected to try again.
        return { ...base, status: 'error', reuploadable: true };
    }

    private _resumeUploads(list: DraftAttachment[]): void
    {
        for (const chip of list)
        {
            if (chip.status !== 'uploading' || typeof chip.operationId !== 'number') continue;

            void this._fileService
                .resumeUpload(chip.operationId, { name: chip.name, mimeType: chip.mimeType, fileSize: chip.size })
                .then((attachment) => this._patch(chip.id, { status: 'ready', attachment, operationId: undefined }))
                .catch(() => this._patch(chip.id, { status: 'error', reuploadable: true, operationId: undefined }));
        }
    }

    private _persist(list: DraftAttachment[]): void
    {
        try
        {
            if (list.length === 0)
            {
                localStorage.removeItem(STORAGE_KEY);
                return;
            }
            localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
        }
        catch
        {
            // Storage unavailable — attachment persistence is best-effort.
        }
    }

    private _append(item: DraftAttachment): void
    {
        this._drafts.update((list) => [ ...list, item ]);
    }

    private _patch(id: string, patch: Partial<DraftAttachment>): void
    {
        this._drafts.update((list) => list.map((a) => (a.id === id ? { ...a, ...patch } : a)));
    }
}
